/* eslint-disable jsx-a11y/no-static-element-interactions */
/**
 * BulkAgentOperationsPage - List and Detail pages for bulk agent chat operations
 *
 * Shows a list of past and current bulk agent chat thread operations with their status and results.
 */

import {
  ArrowLeft,
  BookmarkPlus,
  Bot,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  Download,
  EllipsisVertical,
  ExternalLink,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  X,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AgentChatProvider, AgentChatThread } from '@/components/agent-chat';
import { DataTable } from '@/components/common/DataTable';
import type {
  FilterOption,
  TableAction,
  TableColumn,
} from '@/components/common/DataTable/types';
import { EditorProvider } from '@/components/demands-v2/editor/context/editor-provider';
import { MatterLink } from '@/components/entity-links';
import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import type { PaginationState } from '@/hooks/usePagination';
import { usePagination } from '@/hooks/usePagination';
import { cn } from '@/lib/utils';
import { NotionPushModal } from '@/pages/NotionPushModal';
import { useBulkAgentOperationFilterOptionsQuery } from '@/services/api/bulkAgentThreadsServiceEnhanced';
import {
  type AgentChatThreadSchema,
  type BulkAgentChatOperationSchema,
  type BulkAgentChatOperationStatus,
  enhancedApi,
  type ListBulkAgentChatOperationsApiArg,
  type OrchestratorStatus,
  type TableDefinition,
  useListBulkAgentChatOperationsQuery,
} from '@/services/api/generated/bulkAgentThreadsService';
import { humanizeColumnId } from '@/utils/formatting';
import { getResults, getTotalCount } from '@/utils/pagination';

// ======================= Template Storage =======================

const TEMPLATES_STORAGE_KEY = 'bulk-finchgpt-templates';

export interface BulkOperationTemplate {
  id: string;
  name: string;
  title: string;
  initialTask: string;
  tableDefinition: TableDefinition | null;
  createdAt: string;
  isDefault?: boolean;
}

// Default template - Firm Case Review
const DEFAULT_TEMPLATES: BulkOperationTemplate[] = [
  {
    id: 'default-firm-case-review',
    name: 'Firm Case Review',
    title: 'Firm Case Review',
    initialTask: `This prompt runs TWO case health analyses on this matter:

  1. EARLY CASE CLASSIFICATION (First 14-Day Risk Detection)
     Surfaces liability concerns, insurance gaps, carrier denials, and
     client responsiveness issues during the critical early window.

  2. OVER-TREATMENT DETECTION (Medical Expense vs. Coverage Limit Analysis)
     Evaluates whether accumulated medical expenses are approaching or
     exceeding available insurance coverage limits.

Complete all steps in order. Every finding must include the SOURCE
(document name, note date, message timestamp, coverage record field).
Do not assume - if evidence is not found, report it as missing.

IMPORTANT - TASK NOTES: For every data point, search BOTH the main file
documents AND the notes/comments attached to tasks. Task notes frequently
contain critical context (carrier call results, adjuster names, denial
language, escalation confirmations, client statements) that does not appear
in formal documents. Never skip task notes as a source.

IMPORTANT CONTEXT:
- We are a case management company managing PI cases on behalf of law
  firms. We handle case setup, insurance carrier communications,
  treatment coordination, and client contact.
- The early classification steps should note if the case is outside the
  first 14-day window - findings are still valid but the early
  intervention window has passed.

=======================================================================
PART 1: EARLY CASE CLASSIFICATION
=======================================================================

STEP 1: CASE IDENTITY AND TIMING
Establish the baseline identity and determine how far into the early
window this case is:
- Client full name
- Law firm name
- Assigned case manager
- Current phase in the pipeline
- Date of loss (accident / incident date)
- Date of intake (when the matter was created or signed)
- Days since intake (how far into the early window we are)
- Incident state (determines coverage rules and requirements)

If the case is > 14 days old, flag: "Outside early classification
window - [X] days since intake. Findings still valid but early
intervention window has passed."

STEP 2: LIABILITY ASSESSMENT
Determine whether there are ANY liability concerns on this matter.
Search ALL available sources: police report, intake notes, carrier
correspondence, LOA documents, adjuster letters, coverage records,
transcripts, task notes, and activity log entries.

A) FAULT DETERMINATION
Look for any evidence about who is at fault:
- Police report fault determination (if on file)
- Carrier fault position: Accepted / Disputed / Denied / Under investigation
- Client account: any admissions of partial fault
- Witness statements or other evidence

B) LIABILITY RED FLAGS
Flag if ANY of the following are present:
- Client cited in police report
- Police report assigns shared fault or is ambiguous
- Client admits any degree of fault in intake or conversations
- Rear-end collision WHERE OUR CLIENT IS THE REAR DRIVER
- Multi-vehicle accident with disputed chain of causation
- Disputed lane change, merge, or intersection right-of-way
- Incident on private property where fault rules differ
- Pedestrian/cyclist case with possible contributory negligence
- Carrier has communicated that liability is disputed or denied
- Word-against-word with no independent witness or police report
- Hit and run where at-fault party is unidentified
- Government entity or commercial vehicle involved
- Any language suggesting comparative fault, shared liability,
  under investigation, or pending review

C) LIABILITY CLASSIFICATION
- "Clear - Accepted": Carrier accepted or police report clearly assigns
  fault to the other party. No red flags.
- "Clear - Likely": Facts strongly favor our client. No red flags.
- "Concern - Disputed": At-fault carrier explicitly disputed liability.
- "Concern - Comparative Fault": Evidence suggests shared fault.
- "Concern - Denied": Carrier denied the claim outright on liability.
- "Concern - Under Investigation": No liability determination yet.
- "Concern - Unclear": Facts are ambiguous or conflicting.
- "Unknown - No Data": No liability information found on the file.
- "N/A - No 3P": No third-party coverage or claim applicable.

D) LIABILITY RISK LEVEL
- "None": Clear - Accepted or Clear - Likely
- "Low": Under Investigation with favorable facts
- "Medium": Under Investigation with ambiguous facts, or Unclear
- "High": Disputed, Comparative Fault, or Denied
- "Critical": Denied with no alternative recovery, or client at fault

STEP 3: INSURANCE COVERAGE GAPS
Check whether BOTH 1st-party and 3rd-party insurance have been
identified and documented on this matter.

A) 3RD-PARTY INSURANCE STATUS
- "Identified - LOR Sent": Carrier known, LOR sent, progressing.
- "Identified - LOR Not Sent": Carrier known, LOR not sent. Setup failure.
- "Not Identified - Active Search": No carrier, but effort underway.
- "Not Identified - No Effort": No carrier, no effort. Setup failure.
- "Uninsured - Confirmed": Other driver confirmed uninsured.
- "Hit and Run - At-Fault Unknown": At-fault party not identified.
- "N/A": No 3rd-party claim applicable.

B) 1ST-PARTY INSURANCE STATUS
- "Identified - LOR Sent": Carrier known, LOR sent, progressing.
- "Identified - LOR Not Sent": Carrier known, LOR not sent.
- "Not Identified - Active Search": No carrier, effort underway.
- "Not Identified - No Effort": No carrier, no effort.
- "Client Uninsured - Confirmed": Client has no auto insurance.
- "N/A": 1P coverage not applicable for this case type.

C) COVERAGE COMPLETENESS SCORE
- "Complete": Both 1P and 3P identified, LORs sent, progressing.
- "Partial": One side identified, the other missing or in progress.
- "Minimal": One or both sides not identified, minimal effort.
- "Empty": No insurance information on the file at all.

STEP 3B: PIP AND MEDPAY ASSESSMENT
Deep-dive into the medical payment coverages that fund treatment.
PIP (Personal Injury Protection) and MedPay (Medical Payments) are
critical for treatment authorization and billing. Assess each separately.

A) PIP STATUS
- Carrier name (usually same as 1P carrier)
- Policy limit (dollar amount)
- State PIP filing deadline (date by which PIP application must be submitted
  per state statute — e.g., NY = 30 days from DOL, FL = 14 days from DOL)
- PIP application submitted: Yes / No (date submitted if yes)
- Days remaining until PIP statute deadline (calculate from DOL)
- LOR sent: Yes / No (do not include date sent)
- LOA received: Yes / No (include date received)
- Amount used or billed to date (if documented)
- Exhaustion risk: "Within Limits" / "Approaching" / "Exhausted" /
  "Unknown - No Data"
- Source of limit (LOA, carrier letter, coverage record)

B) MEDPAY STATUS
- Carrier name (usually same as 1P carrier; MedPay is often on same
  policy as PIP)
- Policy limit (dollar amount)
- LOR sent: Yes / No (do not include date sent)
- LOA received: Yes / No (include date received)
- Amount used or billed to date (if documented)
- Exhaustion risk: "Within Limits" / "Approaching" / "Exhausted" /
  "Unknown - No Data"
- Source of limit (LOA, carrier letter, coverage record)

NOTE: Assess PIP and MedPay independently. Do NOT combine them into a
single coordination section. Each has its own carrier, limit, filing
status, and exhaustion risk that must be tracked separately.

C) PIP GAPS - Flag any of the following for PIP specifically:
- PIP not identified but case is in a mandatory PIP state
- PIP application not submitted and deadline is approaching or passed
- PIP limit unknown despite LOA on file
- LOR not sent to 1P carrier (blocks PIP billing)
- No documentation of amounts billed to PIP
- PIP denial not yet addressed

D) MEDPAY GAPS - Flag any of the following for MedPay specifically:
- MedPay not identified but policy suggests it may exist
- MedPay limit unknown despite coverage record on file
- LOR not sent to MedPay carrier
- No documentation of amounts billed to MedPay
- MedPay denial not yet addressed

STEP 4: CLAIM DENIALS
Search for any evidence that a carrier has DENIED the claim, denied
coverage, or refused to open a claim file.
Search: LOA documents, carrier correspondence, adjuster letters,
notes documenting carrier calls, coverage records, task notes.

A) 3RD-PARTY DENIAL TYPES
- LIABILITY DENIAL: Carrier denies their insured is at fault.
- COVERAGE DENIAL: Claim not covered under policy terms.
- NO COVERAGE / NO POLICY: No active policy at time of accident.
- DISPUTED LIABILITY: Carrier asserts comparative/reduced fault.
- FILE REFUSAL: Carrier refuses to open a claim file.

B) 1ST-PARTY DENIAL TYPES
- PIP / MEDPAY DENIAL: PIP or MedPay benefits denied.
- UM/UIM DENIAL: UM/UIM claim denied.
- COVERAGE DENIAL: Policy does not cover this claim.
- ELIGIBILITY DENIAL: Client not eligible for benefits.
- IME-BASED DENIAL: Benefits denied based on IME findings.

C) DENIAL INVENTORY
For EACH denial found, record: carrier, 1P or 3P, denial type,
date of denial, key language, source document, and whether our team responded.
Include dates on all denial records.

D) DENIAL STATUS
- "No Denials": No evidence of any claim denial.
- "3P Denial": At-fault carrier has denied (specify type).
- "1P Denial": Client carrier has denied (specify type).
- "Both Denied": Denials from both 1P and 3P carriers.
- "Partial Denial": Partial liability or specific benefit denied.

STEP 5: CLIENT RESPONSIVENESS
Determine whether the client is reachable and responsive during the
critical first 14 days.

A) Count every outbound contact attempt since intake:
calls, texts, emails, voicemails, and any other documented outreach.
Note dates, channels, and whether each attempt was successful.

B) Count every client response or client-initiated contact.
Record total responses, date of most recent response, and days since
last response (or days since intake if client has never responded).

C) RESPONSIVENESS PATTERN
- "Engaged": Substantive two-way communication established.
- "Partially Responsive": Responds to some outreach, inconsistent.
- "Minimally Responsive": Responded once or twice, hard to reach.
- "Unresponsive": Zero responses despite multiple attempts.
- "Never Attempted": We have not attempted to contact the client.
- "Single Attempt Only": Only one attempt made.

D) UNREACHABLE CLIENT SIGNALS - Flag any of the following:
- 3+ outbound attempts with zero client responses
- Client has not responded in 7+ days since intake
- Voicemails not returned within 48 hours
- Text messages not replied to within 48 hours
- Phone goes straight to voicemail every attempt
- Notes indicating "client unreachable" or "unable to reach client"
- Open callback tasks with no completion

E) CLIENT RESPONSIVENESS RISK
- "None": Engaged, responsive, two-way communication established.
- "Low": Partially responsive - slow but eventually replies.
- "Medium": Minimally responsive - hard to reach.
- "High": Unresponsive - multiple attempts, no response.
- "Critical": Unresponsive 7+ days post-intake despite 5+ attempts.
- "N/A - Our Fault": We have not attempted to contact the client.

STEP 6: COMPOSITE EARLY RISK ASSESSMENT
Combine all findings into an overall early case risk picture.

A) RISK FLAGS - count active risk factors:
- Liability concern (any classification other than Clear)
- 3P insurance missing or unidentified
- 1P insurance missing or unidentified
- 3P claim denial
- 1P claim denial
- Client unresponsive or minimally responsive
- Case > 7 days old with empty or minimal coverage documentation
- No LOR sent to any carrier despite 7+ days since intake

B) OVERALL EARLY RISK TIER
- "Low Risk": 0-1 minor flags. Case setup on track.
- "Moderate Risk": 1-2 flags, none critical.
- "High Risk": 2-3 flags including liability concern, denial, or
  unresponsive client.
- "Critical Risk": 3+ flags, or any single critical flag.

C) PRIORITY ACTION: The SINGLE most important action RIGHT NOW.
Must be specific, actionable, and directed at a person.

D) SECONDARY ACTIONS: Up to 3 additional actions in priority order.

E) ESCALATION NEEDED
- "No": Case manager can handle all identified issues.
- "Yes - Managing Paralegal": Liability dispute, denial, or persistent
  client unresponsiveness.
- "Yes - Firm / Attorney": Liability denied, case viability in question,
  client may need to be released.

=======================================================================
PART 2: OVER-TREATMENT DETECTION
=======================================================================

Evaluate this matter for over-treatment risk. Over-treatment occurs when
accumulated medical expenses approach or exceed available policy limits.
Every dollar spent past coverage limits is unrecoverable and directly
harms case value. Catch this BEFORE it happens, not after.

STEP 7: GATHER TOTAL MEDICAL EXPENSES
- Pull every expense record. Include paid and outstanding/pending.
- Review documents for bills not yet captured as expense records.
- Produce: TOTAL_EXPENSES_RECORDED and TOTAL_BILLS_IN_DOCUMENTS.
- Note any gap between the two (may indicate unentered bills).
- Count distinct medical providers. Flag any single provider whose
  charges represent more than 50% of total expenses.

STEP 7B: CATALOG TREATMENT TYPES
For each treatment type found, note frequency and approximate cost.

Treatment types to look for:
- DIAGNOSTIC IMAGING: MRI, CT scan, X-ray, ultrasound
- INJECTIONS: epidural steroid, trigger point, facet joint, nerve
  blocks, PRP injections
- CHIROPRACTIC: adjustments, spinal manipulation
- PHYSICAL THERAPY: PT sessions, rehabilitation exercises
- SURGERY: any surgical procedure - note specific procedure name
- EMERGENCY / URGENT CARE: ER visits, urgent care, ambulance
- PAIN MANAGEMENT: office visits, medication management
- SPECIALIST VISITS: orthopedic, neurologist, neurosurgeon, etc.
- DME: braces, TENS units, other durable medical equipment
- OTHER: any treatment not listed above

Flag combinations that may indicate over-treatment patterns:
- High-cost imaging without corresponding specialist referral
- Excessive injection frequency (more than 3 rounds of same type)
- Chiropractic visit counts significantly above typical (> 40 visits)
- Surgery recommended or performed on a low-limit policy case
- Multiple overlapping treatment modalities with same provider

STEP 8: DETERMINE 1ST-PARTY COVERAGE LIMITS
Look at all coverages with category = FIRST_PARTY (PIP, MedPay,
No-Fault, Personal Injury Protection).
- Identify each carrier name and extract policy limits.
- Check LOA, carrier correspondence, and coverage documents.
- If LOA is on file but limits not captured, flag as data gap.
- If no 1st-party coverage exists, note "None on file."

STEP 9: DETERMINE 3RD-PARTY COVERAGE LIMITS
Look at all coverages with category = THIRD_PARTY.
- Identify each carrier name and extract BI policy limits.
- If LOA received but limits not captured, flag as data gap.
- If limits unknown, use the vehicle type fallback below.

VEHICLE TYPE FALLBACK (when 3rd-party limits are unknown):
- Commercial vehicle (semi, delivery van, company car): $750K-$1M+
- Standard passenger vehicle: $25K-$100K typical. Higher risk.
- Uninsured / unknown vehicle: Maximum over-treatment risk.
- Government or municipal vehicle: Tort claim caps may apply.
- Rideshare (Uber, Lyft): Typically $1M liability when on-trip.

Record vehicle type, make/model, and commercial markings even if
limits remain unknown.

STEP 10: OVER-TREATMENT RISK ASSESSMENT
Calculate EXPENSE-TO-LIMIT RATIO: ratio = total_expenses / policy_limit
Use the MOST CONSERVATIVE (worst-case) ratio across all coverages.

Risk thresholds:
- < 50% of limits: "Within Range" - treatment proportionate.
- 50-75% of limits: "Approaching Limits" - monitor closely.
- 75-100% of limits: "Near Limits" - evaluate carefully.
- > 100% of limits: "Exceeds Limits" - CRITICAL, immediate review.
- Limits unknown + high expenses: "Indeterminate - High Exposure."

STEP 11: RECOMMENDED ACTION
- "Within Range": No action needed. Continue treatment as planned.
- "Approaching Limits": Monitor costs. Review treatment plan for
  cost efficiency.
- "Near Limits": Treatment cost review recommended. Coordinate with
  firm on whether continued treatment is strategically justified.
- "Exceeds Limits": URGENT - recommend immediate treatment freeze
  pending firm strategy discussion.
- "Indeterminate - High Exposure": Prioritize obtaining policy limits
  before authorizing further treatment.`,
    tableDefinition: {
      columns: [
        {
          id: 'readout_status',
          prompt: `Return exactly one of: On Track, On Watch, Escalation.

Use ESCALATION for any of the following — these require attorney awareness NOW:
- Any carrier denial (liability, coverage, PIP/MedPay, UM/UIM, or IME-based)
- Liability disputed or denied by 3P carrier
- Client at fault or comparative fault risk identified
- Client unresponsive 7+ days post-intake despite 3+ attempts
- SOL risk, missed deadline, or critical filing window approaching
- PIP/No-Fault deadline missed or within 5 days of expiration
- Medical expenses at or exceeding policy limits (Over-Treatment Risk: Exceeds Limits)
- Government or commercial vehicle with notice-of-claim requirements unmet
- Case viability in question (attorney may need to evaluate dropping the case)
- Escalation Needed field = "Yes - Firm / Attorney" per composite risk assessment
- Case has already been escalated to firm/attorney and is pending response
- Refund risk is High — client has explicitly requested a refund or stated intent to leave
- Active bankruptcy filing identified — potential stay on recovery proceeds
- Child support or other lien identified that materially affects recovery
- Prior attorney lien undocumented or unresolved
- Injury severity is critical (surgery performed/recommended, hospitalization,
  catastrophic injury) and no attorney strategy discussion is on file

Use ON WATCH for any of the following — meaningful blockers requiring active tracking:
- 3P or 1P carrier not yet identified beyond Day 5 since intake
- LOR not sent to any carrier beyond Day 7 since intake
- Liability under investigation with no carrier position communicated after Day 10
- PIP/MedPay application not submitted with state deadline within 14 days
- Client minimally responsive or last contact more than 5 days ago
- Policy limits unknown with total medical expenses approaching $15,000+
- Medical expenses 50-75% of known policy limits (Approaching Limits)
- Medical expenses 75-100% of known policy limits (Near Limits)
- Open denial on file not yet addressed or responded to by our team
- Over-Treatment Risk = Approaching Limits or Near Limits
- Escalation Needed field = "Yes - Managing Paralegal"
- Escalation flagged in notes but not yet confirmed sent to firm/attorney
- Refund risk is Medium — client expressed dissatisfaction or mentioned refund
- Prior attorney identified but substitution or lien status not yet documented
- Bankruptcy referenced in notes but filing status unconfirmed
- Medical bills present but injury documentation is missing or inconsistent

Use ON TRACK only when ALL of the following are true:
- Coverage identified on both sides, LORs sent, LOAs received or actively awaited
- No liability concerns, or liability clearly accepted by 3P carrier
- No carrier denials, or any denial has been formally addressed and responded to
- Client engaged and responsive (last contact within 5 days)
- Medical expenses well within known policy limits (under 50%)
- No critical deadlines approaching within 14 days
- Early Risk Tier = Low Risk or Moderate Risk with no critical individual flags
- No active escalation pending firm/attorney response
- No bankruptcy, lien, prior attorney, or refund signals on file`,
        },
        {
          id: 'update',
          prompt: `Write a Commentary for the attorney. This is the primary field they
read in the Notion readout — it must surface every red flag, denial,
coverage gap, escalation trigger, and treatment risk in one tight block.

FORMAT: 2-5 bullet points. Each bullet must contain at least one
concrete fact: a date, a dollar amount, a carrier name, a count, or
a specific action taken or missing. No headers. No paragraphs. No filler.

REQUIRED — include a bullet for each of the following IF present on the file:

PRIOR ESCALATION (always surface this first if present)
- If already escalated to firm/attorney: include the date escalated,
  who it was directed to, and current status (pending response / firm
  engaged / resolution documented)
  e.g., "Escalated to firm 01/11/26 (Liability Task — J. Martinez) —
  pending response; no attorney direction documented as of today."
- If escalation flagged in notes but not confirmed sent: flag it
  e.g., "Escalation noted internally (01/09/26) — no confirmation it
  reached firm/attorney; needs follow-up."

DENIALS & DISPUTES
- Any carrier denial: type, carrier name, date issued, and whether our
  team has responded (e.g., "State Farm liability denial 03/09/26 — no
  response sent by our team")
- Liability disputed or under investigation: carrier position, date
  communicated, and supporting or conflicting evidence
- Partial fault assertion or comparative liability language from carrier

COVERAGE GAPS & DEADLINE RISK
- 3P or 1P carrier not identified: days since intake, effort underway?
- LOR not sent to any carrier: days since intake
- PIP/No-Fault application not submitted: state deadline date, days remaining
- Government/MTA/EMS/commercial entity: Notice of Claim not sent
- LOA not yet received: carrier name, days since LOR was sent

OVER-TREATMENT / POLICY LIMIT RISK
- Total medical expenses vs. known policy limits: ratio and dollar amounts
  (e.g., "$38K expenses vs. $50K State Farm BI — 76%")
- PIP or MedPay exhaustion risk: amount used, limit, percentage consumed
- Treatment pattern flags: excessive injections, surgery on low-limit policy,
  high-cost imaging without specialist referral
- Limits unknown with high expense exposure: vehicle type and estimated range

CLIENT RESPONSIVENESS
- Unresponsive or minimally responsive: number of attempts, days since
  last response, channels tried
- Emergency contact or final attempt triggered

BANKRUPTCY & LIENS
- Bankruptcy filing (active or recent): chapter, filing date, case number
  if known, and whether a bankruptcy stay may affect recovery
  e.g., "Client filed Chapter 7 bankruptcy 02/14/26 — potential stay on
  recovery; attorney must determine impact on case proceeds."
- Child support lien: lien amount if known, issuing agency, and whether
  it attaches to PI proceeds
  e.g., "Child support lien on file — $8,400 owed to [agency]; attaches
  to settlement proceeds; attorney must account for this in demand strategy."
- Any other lien type (medical, government, ERISA): lienholder, amount, status
- If no lien or bankruptcy information found: omit this bullet entirely

PRIOR / CURRENT ATTORNEYS
- Prior attorney on this matter: name/firm, reason for discharge or
  withdrawal, date of substitution, and whether prior counsel's lien
  is documented
  e.g., "Prior attorney [Firm Name] — substituted out 01/20/26; potential
  attorney's lien not yet documented."
- Concurrent representation red flag: any indication client may have
  another attorney actively involved
- If no prior/concurrent attorney information found: omit this bullet entirely

MEDICAL BILLS & INJURY SEVERITY
- Current total medical bill amount: sum of all documented expenses
  (paid + outstanding), source of the figure, and date of last update
  e.g., "Total medical bills: $22,400 as of 03/10/26 (expense records +
  2 uploaded provider bills)."
- Severity of injuries: summarize the most serious documented injuries
  in plain terms — include body parts, diagnoses, whether imaging or
  surgery is involved, and treating providers if known
  e.g., "Injuries: lumbar disc herniation (L4-L5) confirmed on MRI
  02/28/26, cervical strain, right shoulder sprain — treating with
  [Chiropractor] and [Pain Mgmt]; surgery not yet recommended."
- Flag if injury severity appears disproportionate to documented bills,
  or if serious injuries are documented but no treatment has been initiated
- If no medical bill or injury information found: note "No medical expenses
  or injury documentation on file — treatment status unknown."

REFUND / CLIENT DISSATISFACTION
- Any refund language or client intent to leave: source, date, context

ATTORNEY ESCALATION TRIGGERS (if not already escalated)
- Liability denied or case viability in question: include carrier, date,
  and recommended attorney action
- Expenses exceed limits: include overage amount and treatment freeze
  recommendation
- Client may need to be released: include reason and days unresponsive

POSITIVE STATUS (only include if NO red flags exist in that category)
- Coverage complete: LORs sent, LOA received — only if actually true
- Client engaged: last contact date — only if responsive
- No prior escalation, no liens, no prior attorneys, no refund signals —
  only if confirmed

END with ONE sentence stating the single most important action the
attorney or managing paralegal must take, and who owns it.
Examples:
- "FOLLOW UP on existing escalation: firm was notified 01/11/26 —
  no response or direction documented; [CM Name] must confirm receipt."
- "ESCALATE to [Firm]: State Farm denied liability 03/09/26 — no
  response sent; attorney must determine case viability."
- "URGENT: expenses ($55K) exceed GEICO BI limits ($50K) by $5K —
  recommend immediate treatment freeze pending firm strategy discussion."
- "[CM Name] must submit PIP application to GEICO today — NY 30-day
  deadline expires 03/23/26 (13 days remaining)."`,
        },
        {
          id: 'Client Name',
          prompt:
            'Client\'s full name.',
        },
        {
          id: 'Firm Name',
          prompt:
            'Name of the law firm.',
        },
        {
          id: 'Case Manager',
          prompt:
            'Assigned case manager.',
        },
        {
          id: 'Current Phase',
          prompt: `Pull the current matter status/phase DIRECTLY from the matter status field in the system.
Do not infer or guess based on activity — read the actual status field.
Common values: Case Ingestion, Initial Review, Case Setup, Treatment, Demand, Settled,
Closed, Attorney Withdrawn, File Set-Up, Terminated.
Output the exact status as it appears in the matter record.
If the status field is blank or not found, output: "Status not found in matter record."`,
        },
        {
          id: 'Date of Loss',
          prompt:
            'Date of the accident / incident (MM/DD/YYYY).',
        },
        {
          id: 'Date of Intake',
          prompt:
            'Date the matter was created or signed (MM/DD/YYYY).',
        },
        {
          id: 'Days Since Intake',
          prompt:
            'Number of days from intake to today. Flag if > 14: \'Outside early window - [X] days.\'',
        },
        {
          id: 'Incident State',
          prompt:
            'U.S. state where the incident occurred.',
        },
        {
          id: 'Liability Classification',
          prompt: `One of:
- "Clear - Accepted" - fault on other party, carrier accepted or police report clear
- "Clear - Likely" - facts strongly favor our client, no red flags
- "Concern - Disputed" - at-fault carrier explicitly disputed liability
- "Concern - Comparative Fault" - evidence suggests shared fault
- "Concern - Denied" - carrier denied the claim based on liability
- "Concern - Under Investigation" - no liability determination yet
- "Concern - Unclear" - facts are ambiguous or conflicting
- "Unknown - No Data" - no liability information found on the file
- "N/A - No 3P" - no third-party claim applicable`,
        },
        {
          id: 'Liability Red Flags',
          prompt: `List every liability red flag found, one per line with source:
e.g.:
- Client cited in police report (Report #2025-12345, filed 01/10/26)
- Carrier states "liability under investigation" (LOA from State Farm, 01/20/26)
- Client mentioned "I might have been going a little fast" (intake transcript 01/08/26)
If no red flags: "None identified - liability appears clear."`,
        },
        {
          id: 'Liability Risk Level',
          prompt: `One of:
- "None" - Clear - Accepted or Clear - Likely
- "Low" - Under Investigation with favorable facts
- "Medium" - Under Investigation with ambiguous facts, or Unclear
- "High" - Disputed, Comparative Fault, or Denied
- "Critical" - Denied with no alternative recovery, or client primarily at fault`,
        },
        {
          id: 'Liability Detail',
          prompt: `2-3 sentence explanation of the liability situation. Include who is at fault
and how we know (or do not), any carrier position communicated, and key evidence
supporting or undermining liability. If no info: "No liability information found
on file. Police report status unknown. No carrier correspondence regarding fault."`,
        },
        {
          id: '3P Carrier',
          prompt:
            'Name of the at-fault party\'s insurance carrier. \'Not Identified\' if no 3P carrier on file. \'N/A\' if no 3P claim applicable.',
        },
        {
          id: '3P Insurance Status',
          prompt: `One of:
- "Identified - LOR Sent" - carrier known, LOR sent
- "Identified - LOR Not Sent" - carrier known, LOR not yet sent
- "Not Identified - Active Search" - no carrier, effort underway
- "Not Identified - No Effort" - no carrier, no effort to find it
- "Uninsured - Confirmed" - other driver confirmed uninsured
- "Hit and Run - At-Fault Unknown" - at-fault party not identified
- "N/A" - no 3P claim applicable`,
        },
        {
          id: '3P LOA Status',
          prompt: `Has the 3P carrier acknowledged our representation?
- "Received" - LOA received (include date received)
- "Awaiting" - LOR sent, awaiting response (do not include LOR sent date)
- "Not Applicable" - LOR not sent or no 3P carrier
- "N/A" - no 3P claim`,
        },
        {
          id: '1P Carrier',
          prompt:
            'Name of the client\'s own insurance carrier. \'Not Identified\' if no 1P carrier on file. \'Client Uninsured\' if confirmed uninsured.',
        },
        {
          id: '1P Insurance Status',
          prompt: `One of:
- "Identified - LOR Sent" - carrier known, LOR sent
- "Identified - LOR Not Sent" - carrier known, LOR not yet sent
- "Not Identified - Active Search" - no carrier, effort underway
- "Not Identified - No Effort" - no carrier, no effort to find it
- "Client Uninsured - Confirmed" - client has no auto insurance
- "N/A" - 1P coverage not applicable for this case type`,
        },
        {
          id: '1P Coverages on File',
          prompt: `List each 1P coverage type documented, one per line with limit if known:
PIP: $[amount] or Unknown or N/A
MedPay: $[amount] or Unknown or N/A
UM/UIM: $[amount] or Unknown or N/A
If no 1P coverage on file: "None on file."`,
        },
        {
          id: 'PIP Limit',
          prompt:
            'PIP policy limit as dollar amount (e.g., \'$10,000\'). \'Unknown\' if PIP exists but limit not documented. \'N/A\' if no PIP on file or not applicable for this case type.',
        },
        {
          id: 'PIP LOR Status',
          prompt:
            'LOR sent to PIP carrier? \'Sent\' / \'Not Sent\' / \'N/A\' if no PIP. Do not include the date sent.',
        },
        {
          id: 'PIP LOA Status',
          prompt:
            'LOA received from PIP carrier? \'Received\' (include date received) / \'Awaiting\' (LOR sent, do not include LOR sent date) / \'N/A\' if no PIP or LOR not sent.',
        },
        {
          id: 'PIP Amount Used',
          prompt:
            'Dollar amount billed or used against PIP to date, if documented. \'Unknown\' if not documented. \'N/A\' if no PIP.',
        },
        {
          id: 'PIP Exhaustion Risk',
          prompt: `One of:
- "Within Limits" - documented usage well below limit
- "Approaching" - usage approaching limit (e.g., 75%+)
- "Exhausted" - PIP limit fully used
- "Unknown - No Data" - usage not documented
- "N/A" - no PIP on file`,
        },
        {
          id: 'MedPay Limit',
          prompt:
            'MedPay policy limit as dollar amount (e.g., \'$5,000\'). \'Unknown\' if MedPay exists but limit not documented. \'N/A\' if no MedPay on file or not applicable for this case type.',
        },
        {
          id: 'MedPay LOR Status',
          prompt:
            'LOR sent to MedPay carrier? \'Sent\' / \'Not Sent\' / \'N/A\' if no MedPay (often same as 1P/PIP carrier). Do not include the date sent.',
        },
        {
          id: 'MedPay LOA Status',
          prompt:
            'LOA received from MedPay carrier? \'Received\' (include date received) / \'Awaiting\' (LOR sent, do not include LOR sent date) / \'N/A\' if no MedPay or LOR not sent.',
        },
        {
          id: 'MedPay Amount Used',
          prompt:
            'Dollar amount billed or used against MedPay to date, if documented. \'Unknown\' if not documented. \'N/A\' if no MedPay.',
        },
        {
          id: 'MedPay Exhaustion Risk',
          prompt: `One of:
- "Within Limits" - documented usage well below limit
- "Approaching" - usage approaching limit (e.g., 75%+)
- "Exhausted" - MedPay limit fully used
- "Unknown - No Data" - usage not documented
- "N/A" - no MedPay on file`,
        },
        {
          id: 'PIP MedPay Priority',
          prompt:
            'Which coverage pays first for medical bills? State-dependent. e.g., \'PIP primary (no-fault state)\' or \'MedPay primary\' or \'Unknown - state rules not documented\'. \'N/A\' if neither PIP nor MedPay on file.',
        },
        {
          id: 'PIP MedPay Gaps',
          prompt: `List every PIP/MedPay-specific gap found, one per line:
e.g.:
- PIP limit unknown despite LOA on file
- LOR not sent to 1P carrier - blocks PIP/MedPay billing
- No documentation of amounts billed to PIP
- PIP denial (State Farm 01/15/26) not yet addressed
If no gaps: "None - PIP and MedPay documented and progressing."`,
        },
        {
          id: '1P LOA Status',
          prompt: `Has the 1P carrier acknowledged our representation?
- "Received" - LOA received (include date received)
- "Awaiting" - LOR sent, awaiting response (do not include LOR sent date)
- "Not Applicable" - LOR not sent or no 1P carrier
- "N/A" - no 1P coverage`,
        },
        {
          id: 'Coverage Completeness',
          prompt: `One of:
- "Complete" - both 1P and 3P identified, LORs sent, progressing
- "Partial" - one side identified, the other missing or in progress
- "Minimal" - one or both sides not identified, minimal effort
- "Empty" - no insurance information on the file at all`,
        },
        {
          id: 'Insurance Gaps',
          prompt: `List every insurance gap found, one per line:
e.g.:
- 3P carrier not identified - no police report on file
- 1P LOR not sent - carrier identified (GEICO) but no LOR sent after 9 days
- UM/UIM limits unknown - 1P LOA not yet received
If no gaps: "None - both 1P and 3P coverage documented and LORs sent."`,
        },
        {
          id: 'Denial Status',
          prompt: `One of:
- "No Denials" - no evidence of any claim denial
- "3P Denial" - at-fault carrier has denied (specify type)
- "1P Denial" - client carrier has denied (specify type)
- "Both Denied" - denials from both carriers
- "Partial Denial" - specific benefit or partial liability denial`,
        },
        {
          id: 'Denial Details',
          prompt: `For each denial found, list on a separate line:
[1P or 3P] - [Carrier] - [Denial Type] - [Date] - [Key language] - [Source]
e.g.:
3P - State Farm - Liability Denial - 01/18/26 - "Our investigation determined our insured is not liable" - Adjuster letter 01/20/26
If no denials: "None - no denials found on file."`,
        },
        {
          id: 'Denial Response',
          prompt: `For each denial, has our team responded or addressed it?
- "Addressed" - response sent or escalation documented (include date and action)
- "Not Addressed" - no response or follow-up found
- "N/A" - no denials on file`,
        },
        {
          id: 'Outbound Attempts',
          prompt:
            'Total number of outbound contact attempts to the client since intake (calls + texts + emails + voicemails). \'0\' if we have not attempted to contact the client.',
        },
        {
          id: 'Outbound by Channel',
          prompt: `Breakdown of outbound attempts by channel:
Calls: [count] (Answered: [X], Unanswered: [Y])
Texts: [count]
Emails: [count]
Voicemails Left: [count]
If zero attempts: "No outbound contact attempts since intake."`,
        },
        {
          id: 'Client Responses',
          prompt:
            'Total number of client responses or client-initiated contacts since intake. \'0\' if client has not responded to anything.',
        },
        {
          id: 'Last Client Response',
          prompt:
            'Date and type of the most recent client response (e.g., \'01/15/26 - text reply\'). \'Never - client has not responded since intake\' if zero responses.',
        },
        {
          id: 'Days Since Last Response',
          prompt:
            'Days since the client last responded or initiated contact. \'Never\' if client has not responded at all. \'N/A\' if no outreach attempted.',
        },
        {
          id: 'Responsiveness Pattern',
          prompt: `One of:
- "Engaged" - client responds to most outreach, two-way communication established
- "Partially Responsive" - client responds to some outreach, inconsistent
- "Minimally Responsive" - client responded once or twice, generally hard to reach
- "Unresponsive" - zero client responses despite multiple attempts
- "Never Attempted" - we have not attempted to contact the client
- "Single Attempt Only" - only one attempt made, insufficient to classify`,
        },
        {
          id: 'Unreachable Signals',
          prompt: `List every unreachable warning sign found, one per line:
e.g.:
- 5 outbound calls, 0 answered, 0 callbacks (01/08-01/15/26)
- 3 text messages sent, 0 replies (01/08, 01/11, 01/14)
- Note: "Client unreachable" (task note 01/13/26)
If none: "None - client is responsive and reachable."`,
        },
        {
          id: 'Client Responsiveness Risk',
          prompt: `One of:
- "None" - client is engaged and responsive
- "Low" - partially responsive, slow but replies eventually
- "Medium" - minimally responsive, hard to reach
- "High" - unresponsive despite multiple attempts
- "Critical" - unresponsive 7+ days post-intake despite 5+ attempts
- "N/A - Our Fault" - we have not attempted to contact the client`,
        },
        {
          id: 'Active Risk Flags',
          prompt: `Count and list each active risk factor:
e.g.:
3 flags:
1. Liability disputed by 3P carrier (State Farm)
2. 1P carrier not identified - no insurance info on file
3. Client unresponsive - 4 attempts, zero responses in 10 days
If no flags: "0 flags - no early risk factors identified."`,
        },
        {
          id: 'Early Risk Tier',
          prompt: `One of:
- "Low Risk" - 0-1 minor flags, case setup on track
- "Moderate Risk" - 1-2 flags, none critical, needs attention
- "High Risk" - 2-3 flags including liability concern, denial, or unresponsive client
- "Critical Risk" - 3+ flags, or any single critical flag`,
        },
        {
          id: 'Priority Action',
          prompt: `The SINGLE most important action to take RIGHT NOW. Specific, actionable, directed at a person:
e.g.:
- "[CM Name] must call [Client] today - client has not responded to 4 attempts over 10 days."
- "Escalate to [Firm] - State Farm has denied liability."
- "No urgent action. Case setup is on track. Continue standard cadence."`,
        },
        {
          id: 'Secondary Actions',
          prompt: `Up to 3 additional actions in priority order:
e.g.:
1. Request police report from [agency]
2. Attempt client contact via email - phone and text unsuccessful
3. Follow up with 3P carrier for LOA - LOR sent 8 days ago, no response
If none needed: "None - priority action is sufficient."`,
        },
        {
          id: 'Escalation Needed',
          prompt: `One of:
- "No" - case manager can handle all identified issues
- "Yes - Managing Paralegal" - liability dispute, carrier denial, or persistent unresponsiveness
- "Yes - Firm / Attorney" - liability denied, case viability in question
Include the specific reason for escalation.`,
        },
        {
          id: 'Escalation to Attorney Date',
          prompt: `Search ALL notes, task notes, task comments, and activity log entries for any
language indicating the case was escalated to an attorney or firm.
Look for: "escalated to attorney", "escalated to firm", "esc to atty", "referred to
attorney", "attorney notified", "sent to firm", "flagged for attorney", "attorney aware".
If found, output:
Date: [MM/DD/YYYY] - Source: [task name or note type] - Author: [if known] - Language: "[verbatim]"
e.g., "Date: 01/11/26 - Source: Liability Task - Author: J. Martinez - Language: 'escalated to firm, waiting on direction'"
If escalated multiple times, list each occurrence.
If no escalation to attorney found: "Not escalated to attorney."`,
        },
        {
          id: 'Attorney Response',
          prompt: `Search ALL notes, task notes, task comments, and activity log entries for any
evidence that the attorney or firm responded to an escalation or provided direction.
Look for: attorney reply, firm direction, attorney approval, attorney instruction,
"attorney reviewed", "firm responded", "attorney said", "per attorney", "attorney advised".
If found, output:
Date: [MM/DD/YYYY] - Source: [task name or note type] - Author: [if known] - Direction: "[summary of attorney direction]"
e.g., "Date: 01/13/26 - Source: General Notes - Author: unknown - Direction: 'Attorney advised to continue case, dispute liability denial'"
If no attorney response found: "No attorney response documented."
If no escalation occurred: "N/A - case not escalated to attorney."`,
        },
        {
          id: 'Early Classification Summary',
          prompt: `4-5 sentence narrative covering: days since intake and current phase,
liability status, insurance coverage gaps, any claim denials, and client
responsiveness. Written for a manager triaging new cases. Include specific
carrier names, dates, and risk levels. End with the most important action needed.`,
        },
        {
          id: 'Total Expenses Recorded',
          prompt:
            'Total medical expenses from expense records (dollar amount). Sum all paid and outstanding expenses.',
        },
        {
          id: 'Total Bills in Documents',
          prompt:
            'Total medical bills found in uploaded documents (dollar amount). If no bills found, output \'N/A\'.',
        },
        {
          id: 'Expense Discrepancy',
          prompt:
            'Yes / No - whether bills found in documents differ from recorded expenses.',
        },
        {
          id: 'Provider Count',
          prompt:
            'Number of distinct medical providers generating expenses on this matter.',
        },
        {
          id: 'Highest Provider',
          prompt:
            'Name of the provider with the highest total charges.',
        },
        {
          id: 'Highest Provider Amount',
          prompt:
            'Dollar amount for the highest-charging provider.',
        },
        {
          id: 'Diagnostic Imaging',
          prompt: `For each diagnostic imaging study found, output one entry per line:
[Type] - [Provider Name] - [Count] - [Billing Amount if known] - [Billed to: PIP / MedPay / Health Insurance / Unknown]
e.g., "MRI (lumbar spine) - Advanced Imaging Center - 2 studies - $2,400 - Billed to: PIP"
e.g., "X-Ray (cervical) - St. Mary's ER - 1 study - $380 - Billed to: Health Insurance"
Pull provider name from expense records, uploaded bills, or task notes.
If billing amount not found, note "Billing amount unknown".
If none found, output "None".`,
        },
        {
          id: 'Injections',
          prompt: `List each injection type found, one per line:
[Type] - [Count/Rounds] - [Approx. Cost if known]
e.g., "Epidural steroid injection - 3 rounds - $4,500"
If none found, output "None".`,
        },
        {
          id: 'Chiropractic',
          prompt: `For each chiropractic provider found, output one entry per line:
[Provider Name] - [Visit Count] - [Billing Amount if known] - [Billed to: PIP / MedPay / Health Insurance / Unknown]
e.g., "Family Chiropractic - 24 visits - $4,800 - Billed to: PIP"
Pull provider name from expense records, uploaded bills, or task notes.
If billing amount not found, note "Billing amount unknown".
If none found, output "None".`,
        },
        {
          id: 'Physical Therapy',
          prompt:
            'Total physical therapy sessions and approximate total cost. e.g., \'24 sessions - $4,800\'. If none, output \'None\'.',
        },
        {
          id: 'Surgery',
          prompt:
            'Surgical procedure performed or recommended. Include procedure name, date, and cost if known. If none, output \'None\'.',
        },
        {
          id: 'ER / Urgent Care',
          prompt: `For each ER or urgent care facility found, output one entry per line:
[Facility Name] - [Visit Count] - [Billing Amount if known] - [Billed to: PIP / MedPay / Health Insurance / Unknown]
e.g., "St. Joseph ER - 1 visit - $4,200 - Billed to: Health Insurance"
e.g., "CityMD Urgent Care - 2 visits - $600 - Billed to: PIP"
Pull facility name from expense records, uploaded bills, or task notes.
Do NOT include dates. If billing amount not found, note "Billing amount unknown".
If none found, output "None".`,
        },
        {
          id: 'Pain Management',
          prompt: `For each pain management or orthopedic provider found, output one entry per line:
[Provider Name] - [Specialty: Pain Management / Orthopedic / Neurology / Other] - [Visit Count] - [Billing Amount if known] - [Billed to: PIP / MedPay / Health Insurance / Unknown]
e.g., "Metro Pain Associates - Pain Management - 6 visits - $3,600 - Billed to: PIP"
e.g., "Dr. R. Klein, Orthopedics - Orthopedic - 3 visits - $1,800 - Billed to: Health Insurance"
Pull provider name from expense records, uploaded bills, or task notes.
If billing amount not found, note "Billing amount unknown".
If none found, output "None".`,
        },
        {
          id: 'Specialist Visits',
          prompt:
            'Specialist consultations. Include type, count, and approximate cost. If none, output \'None\'.',
        },
        {
          id: 'DME',
          prompt:
            'Durable medical equipment. List items and cost. If none, output \'None\'.',
        },
        {
          id: 'Other Treatment',
          prompt:
            'Any treatment type not listed above. Describe type, count, and cost. If none, output \'None\'.',
        },
        {
          id: 'Health Insurance / Subro',
          prompt: `Identify the client's health insurance carrier and subrogation status.
Output each of the following on a separate line:
- Health Insurance Carrier: [Name] or "None identified" or "Client has no health insurance"
- Health Insurance Source: [where found: intake notes, coverage record, uploaded EOB, task notes]
- HI / Subro Letter Sent: Yes / No / N/A (no health insurance) - do not include date sent
- Subro Lien Confirmed: Yes ([lienholder name] - $[amount if known]) / No / Unknown
e.g.:
Health Insurance Carrier: Blue Cross Blue Shield
Health Insurance Source: Intake notes 01/08/26
HI / Subro Letter Sent: Yes - 01/15/26
Subro Lien Confirmed: Unknown - no response received
If client has no health insurance, output: "No health insurance on file. HI/Subro letter N/A."
Pull from intake notes, coverage records, task notes, and any uploaded EOB or insurance cards.`,
        },
        {
          id: 'Treatment Pattern Flags',
          prompt: `List any concerning treatment patterns found, one per line:
- "MRI performed without specialist referral or follow-up"
- "4 rounds of epidural injections (typical max: 3)"
- "52 chiropractic visits (significantly above typical 24-36)"
- "Surgery recommended on $25K policy limit case"
If no concerning patterns, output "None identified".`,
        },
        {
          id: '1st Party Coverage Type',
          prompt: `Using the coverage data already identified in Part 1 (see "1P Coverages on File"), select the primary coverage type applicable to treatment expenses:
- "PIP" - Personal Injury Protection
- "MedPay" - Medical Payments
- "No-Fault"
- "Other" - describe
- "N/A" - no 1st-party coverage on file`,
        },
        {
          id: '1st Party Limit',
          prompt:
            'Using the coverage data already identified in Part 1 (see \'1P Coverages on File\'), extract the dollar limit for the primary treatment coverage (e.g., \'$10,000\'). \'Unknown\' if coverage exists but limit not documented. \'N/A\' if no coverage.',
        },
        {
          id: '1st Party Limit Source',
          prompt: `One of:
- "LOA" - from the Letter of Acknowledgement
- "Carrier letter" - from carrier correspondence
- "Coverage record" - from coverage data fields
- "Not found" - coverage exists but limit not documented
- "N/A" - no 1st-party coverage on file`,
        },
        {
          id: '3rd Party BI Limit',
          prompt:
            'Using the carrier already identified in Part 1 (see \'3P Carrier\'), extract the bodily injury policy limit (e.g., \'$50,000\'). \'Unknown\' if coverage exists but limit not documented. \'N/A\' if no coverage.',
        },
        {
          id: '3rd Party Limit Source',
          prompt: `One of:
- "LOA" - from the Letter of Acknowledgement
- "Carrier letter" - from carrier correspondence
- "Coverage record" - from coverage data fields
- "Not found" - coverage exists but limit not documented
- "N/A" - no 3rd-party coverage on file`,
        },
        {
          id: '3rd Party Vehicle Type',
          prompt: `One of:
- "Commercial" - semi-truck, delivery van, company vehicle ($750K-$1M+ limits)
- "Passenger" - standard car/SUV ($25K-$100K typical limits)
- "Rideshare" - Uber, Lyft ($1M on-trip)
- "Government" - municipal or government vehicle (tort claim caps may apply)
- "Uninsured" - no insurance identified
- "Unknown" - vehicle information not available
- "N/A" - 3rd-party limits already known, fallback not needed`,
        },
        {
          id: '3rd Party Vehicle Details',
          prompt:
            'Make, model, or other identifying info about the 3rd-party vehicle (e.g., \'Freightliner semi\', \'2019 Honda Civic\'). If not identifiable, output \'N/A\'.',
        },
        {
          id: 'Estimated Limit Range',
          prompt:
            'Estimated coverage range based on vehicle type when actual limits are unknown (e.g., \'$25K-$100K\'). If actual limits are known, output \'N/A\'.',
        },
        {
          id: 'Expense-to-Limit Ratio',
          prompt:
            'Highest expense-to-limit percentage across all coverages (e.g., \'82%\'). Use most conservative coverage. If no limits available, output \'Indeterminate\'.',
        },
        {
          id: 'Coverage Used for Ratio',
          prompt:
            'Which coverage the ratio is calculated against (e.g., \'3rd Party BI - State Farm $50,000\'). If indeterminate, output \'N/A\'.',
        },
        {
          id: 'Over-Treatment Risk Level',
          prompt: `One of:
- "Within Range" - expenses < 50% of limits
- "Approaching Limits" - expenses 50-75% of limits
- "Near Limits" - expenses 75-100% of limits
- "Exceeds Limits" - expenses > 100% of limits
- "Indeterminate - High Exposure" - limits unknown, expenses warrant concern`,
        },
        {
          id: 'Overage Amount',
          prompt:
            'Dollar amount over the coverage limit if expenses exceed limits (e.g., \'$12,500\'). If not exceeded, output \'N/A\'.',
        },
        {
          id: 'Category',
          prompt: `One of:
- "No Issue" - expenses within range, limits known
- "Deficient - Data Gap" - limits unknown, cannot assess risk
- "Approaching - Monitor" - expenses 50-75% of limits
- "We Messed Up" - expenses near or at limits without anyone flagging it
- "CRITICAL - Exceeds Limits" - expenses already exceed coverage`,
        },
        {
          id: 'Over-Treatment Summary',
          prompt:
            '2-3 sentence over-treatment risk summary. Include total expenses, policy limits, and expense-to-limit percentage. Mention vehicle type context if limits are unknown.',
        },
        {
          id: 'Recommended Action',
          prompt: `Specific next step based on risk level with dollar amounts and carrier names:
e.g.:
- "No action needed. Continue treatment as planned."
- "Monitor treatment costs. Expenses are 62% of State Farm BI limits ($50,000)."
- "URGENT: Expenses ($55,000) exceed GEICO BI limits ($50,000) by $5,000. Recommend immediate treatment freeze pending firm strategy discussion."
- "3rd-party limits unknown. Expenses total $35,000. Vehicle is standard passenger car (est. $25K-$100K). Prioritize obtaining policy limits before authorizing further treatment."`,
        },
      ],
    },
    createdAt: '2024-01-01T00:00:00.000Z',
    isDefault: true,
  },
  {
    id: 'default-simple-case-review',
    name: 'Simple Case Review',
    title: 'Simple Case Review',
    initialTask: `Goal: produce a crisp, attorney-readable case review for a firm sync.

First, gather evidence quickly:
- Review tasks, recent client communications, and any relevant files/transcripts.
- Do not guess. If you cannot find something, write "Unknown".

Then fill the structured table row using the table tool (one pass with all columns).

After updating the table row, leave a short thread message (2–4 bullets) that mirrors:
1) the key update (what changed / biggest blocker), and
2) the top next steps (most urgent first).

Writing rules:
- No markdown, no section headers, no all-caps.
- Bullets only (one line each). Keep it tight and scannable.
- Prefer concrete facts: dates, counts, owners, providers, carriers, tasks.
- Prioritize in this order: client communication, medical records/bills, insurance, liens/subrogation, case posture.`,
    tableDefinition: {
      columns: [
        {
          id: 'client_name',
          prompt:
            'Client name. Return the full name if known; otherwise return "Unknown".',
        },
        {
          id: 'readout_status',
          prompt:
            'Return exactly one of: On Track, On Watch, Escalation. Use Escalation only for urgent risk (SOL/deadline, coverage lapse, unreachable client, critical missing records blocking progress). Use On Watch for meaningful blockers or staleness. Otherwise use On Track.',
        },
        {
          id: 'matter_status',
          prompt:
            'Current matter stage (e.g., Treatment, Case Set-up, Demand). Return a short phrase; if unclear, return "Unknown".',
        },
        {
          id: 'last_client_contact',
          prompt:
            'Last client contact date. Return the most recent documented contact as YYYY-MM-DD (date only; no time, no channel text). If unclear, return "Unknown".',
        },
        {
          id: 'update',
          prompt:
            'Update (1–3 bullets). Focus on the biggest delta or blocker. Each bullet should include at least one concrete fact (date, count, provider/carrier, task). No headers, no paragraphs.',
        },
        {
          id: 'next_steps',
          prompt:
            'Next steps (3–6 bullets, most urgent first). Actionable only. Start each bullet with a verb. Include owner if clear (Finch: / Firm:). Name the provider/carrier/task when possible. If a deadline exists, include the date.',
        },
        {
          id: 'flags',
          prompt:
            'Flags for attention. Return either "None" or a semicolon-separated list of short tags with evidence in parentheses. Example: Missing limits (no policy limit on file as of 2026-02-20); Unreachable client (5 outreaches since 2026-02-07).',
        },
        {
          id: 'case_value',
          prompt:
            'Return exactly one of: <25k, 25-100k, 100k+, Unknown. Choose Unknown if there is not enough information.',
        },
      ],
    },
    createdAt: '2024-01-01T00:00:00.000Z',
    isDefault: true,
  },
  {
    id: 'default-case-analysis',
    name: 'Case Analysis',
    title: 'Case Analysis',
    initialTask: `You are a senior personal injury case analyst. Conduct a comprehensive review of this matter by analyzing ALL available documents, medical records, correspondence, and case notes.

## CASE OVERVIEW
Extract and verify:
- Client name, DOB, and contact information
- Date of incident (date of loss)
- Date firm began representation
- Incident type (MVA, Rear-End MVA, Pedestrian, Slip & Fall, Premises Liability, etc.)
- Opposing parties and their insurance carriers
- Current matter status and phase

## LIABILITY ANALYSIS
Assess liability strength by examining:
- How the incident occurred (mechanism of injury)
- Police report status and findings
- Evidence of fault/negligence (witness statements, photos, video, citations)
- Comparative negligence concerns or red flags
- Weaknesses in the liability story
- Rate liability: Strong / Moderate / Weak / Disputed (with justification)

## MEDICAL ANALYSIS
Review all medical documentation to determine:
- Pre-existing conditions (critical for causation arguments)
- Initial treatment and diagnoses immediately post-incident
- Complete treatment timeline with providers
- Current treatment status (Active / Complete / On Hold)
- Treatment gaps > 30 days (flag these specifically)
- Surgical interventions (completed or recommended)
- Permanent impairment ratings or prognosis if documented
- Future medical needs

## DAMAGES ASSESSMENT
Document all damages:
- Medical specials/bills to date (total amount)
- Itemization by provider if available
- Lost wages (past and any future claims)
- Property damage
- Other special damages

## CASE VALUATION SIGNALS
Identify factors affecting value:
- Severity and visibility of injuries
- Policy limits (if known)
- Venue/jurisdiction considerations
- Client presentation factors

## RED FLAGS & CONCERNS
Flag any issues requiring attention:
- Statute of limitations date and days remaining
- Missing critical documentation
- Treatment gaps > 30 days
- Social media concerns
- Prior claims or litigation history
- Credibility issues
- Subrogation/lien concerns
- Coverage disputes

## RECOMMENDED NEXT STEPS
Provide 3-5 specific, actionable next steps for the case team, prioritized by urgency.

## EXECUTIVE SUMMARY
Conclude with a 2-3 paragraph executive summary including:
- Case overview and current posture
- Key strengths and weaknesses
- Preliminary case valuation range if sufficient information exists
- Critical path to resolution`,
    tableDefinition: {
      columns: [
        {
          id: 'Liability Strength',
          prompt:
            'Assess overall liability strength based on available evidence. Return ONE of: Strong, Moderate, Weak, Disputed. Follow with a brief 1-sentence justification in parentheses. Example: "Strong (Clear rear-end collision with police report citing other driver)"',
        },
        {
          id: 'Incident Type',
          prompt:
            'Classify the incident type. Return ONE of: MVA, Rear-End MVA, T-Bone MVA, Pedestrian Accident, Motorcycle Accident, Slip and Fall, Trip and Fall, Premises Liability, Dog Bite, Product Liability, Medical Malpractice, Workplace Injury, Other. If Other, specify in parentheses.',
        },
        {
          id: 'Police Report Status',
          prompt:
            'What is the status of the police report? Return ONE of: Obtained, Requested, Pending, Unavailable, Not Applicable. If obtained, note the report number if visible.',
        },
        {
          id: 'Comparative Negligence Risk',
          prompt:
            'Is there evidence of comparative/contributory negligence that could reduce recovery? Return: Yes, No, or Unclear. If Yes, briefly explain the concern in parentheses.',
        },
        {
          id: 'Primary Injuries',
          prompt:
            'List the primary diagnosed injuries in order of severity. Include ICD-10 codes if documented. Separate multiple injuries with semicolons. Example: "Cervical disc herniation C5-C6; Lumbar strain; Right shoulder rotator cuff tear"',
        },
        {
          id: 'Treatment Status',
          prompt:
            'Current treatment status. Return ONE of: Active Treatment, Treatment Complete, Treatment On Hold, Not Yet Started, Unknown.',
        },
        {
          id: 'Surgical Status',
          prompt:
            'Has surgery been performed or recommended? Return ONE of: Completed (specify procedure), Scheduled (specify date if known), Recommended (specify procedure), Not Indicated, Unknown.',
        },
        {
          id: 'Treatment Gap Flag',
          prompt:
            'Are there gaps in medical treatment greater than 30 days? Return: Yes, No, or Unable to Determine. If Yes, specify the gap period (e.g., "Yes - 45-day gap between 3/15/2025 and 4/30/2025").',
        },
        {
          id: 'Treating Providers',
          prompt:
            'List the medical providers/facilities treating the client. Include provider type (ER, Chiropractor, Orthopedic, PT, etc.). Separate with semicolons.',
        },
        {
          id: 'Policy Limits',
          prompt:
            'Extract known insurance policy limits. Format as: Carrier Name - Limit Amount. Include all applicable policies (liability, UM/UIM, PIP, MedPay). Separate multiple policies with semicolons. Example: "State Farm (Defendant) - $100,000; GEICO (Client UM) - $50,000"',
        },
        {
          id: 'Case Value Range',
          prompt:
            'Based on injuries, liability strength, and documented specials, estimate the settlement value range. Return ONE of: Under $25K, $25K-$50K, $50K-$100K, $100K-$250K, $250K-$500K, $500K-$1M, Over $1M, Insufficient Data.',
        },
        {
          id: 'Red Flags',
          prompt:
            'List any case concerns or red flags. Include: SOL issues, credibility concerns, coverage gaps, missing documentation, treatment gaps, prior claims, social media issues, lien concerns. Separate with semicolons. Return "None identified" if clean.',
        },
        {
          id: 'Next Critical Action',
          prompt:
            'What is the single most important next step for this case right now? Be specific and actionable. Example: "Request police report from Houston PD - Report #2025-123456"',
        },
        {
          id: 'Case Health Score',
          prompt:
            'Rate overall case health on a scale of 0-100 based on: documentation completeness, liability strength, treatment compliance, deadline management. Provide score with brief breakdown. Example: "75/100 (Liability: Strong, Docs: 80% complete, Treatment: Active, SOL: 400+ days)"',
        },
      ],
    },
    createdAt: '2024-01-01T00:00:00.000Z',
    isDefault: true,
  },
];

export const getTemplates = (): BulkOperationTemplate[] => {
  try {
    const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    const userTemplates: BulkOperationTemplate[] = stored
      ? JSON.parse(stored)
      : [];
    // Filter out stale copies of default templates that leaked into localStorage.
    const defaultIds = new Set(DEFAULT_TEMPLATES.map((t) => t.id));
    const filteredUserTemplates = userTemplates.filter(
      (t) => !t.isDefault && !defaultIds.has(t.id),
    );
    // Default templates first, then user templates
    return [...DEFAULT_TEMPLATES, ...filteredUserTemplates];
  } catch {
    return DEFAULT_TEMPLATES;
  }
};

export const getUserTemplates = (): BulkOperationTemplate[] => {
  try {
    const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const getDefaultTemplates = (): BulkOperationTemplate[] => {
  return DEFAULT_TEMPLATES;
};

export const getTemplateById = (
  id: string,
): BulkOperationTemplate | undefined => {
  return getTemplates().find((t) => t.id === id);
};

export const saveTemplate = (
  template: Omit<BulkOperationTemplate, 'id' | 'createdAt'>,
): BulkOperationTemplate => {
  const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
  const userTemplates: BulkOperationTemplate[] = stored
    ? JSON.parse(stored)
    : [];
  const newTemplate: BulkOperationTemplate = {
    ...template,
    id: `template-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    createdAt: new Date().toISOString(),
  };
  userTemplates.unshift(newTemplate);
  localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(userTemplates));
  return newTemplate;
};

export const deleteTemplate = (id: string): void => {
  // Don't allow deleting default templates
  if (id.startsWith('default-')) return;
  const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
  const userTemplates: BulkOperationTemplate[] = stored
    ? JSON.parse(stored)
    : [];
  const filtered = userTemplates.filter((t) => t.id !== id);
  localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(filtered));
};

// ======================= Components =======================

// Status badge component for operation status
const OperationStatusBadge: React.FC<{
  status: BulkAgentChatOperationStatus;
}> = ({ status }) => {
  switch (status) {
    case 'completed':
      return (
        <Badge color="green" className="gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Completed
        </Badge>
      );
    case 'running':
      return (
        <Badge color="blue" className="gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Running
        </Badge>
      );
    case 'failed':
      return (
        <Badge color="red" className="gap-1.5">
          <XCircle className="h-3.5 w-3.5" />
          Failed
        </Badge>
      );
    case 'pending':
    default:
      return (
        <Badge color="zinc" className="gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          Pending
        </Badge>
      );
  }
};

// Status badge component for thread orchestrator status
const ThreadStatusBadge: React.FC<{ status: OrchestratorStatus }> = ({
  status,
}) => {
  switch (status) {
    case 'completed':
      return (
        <Badge color="green" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Done
        </Badge>
      );
    case 'running':
      return (
        <Badge color="blue" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Running
        </Badge>
      );
    case 'failed':
      return (
        <Badge color="red" className="gap-1">
          <XCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
    case 'unknown':
      return (
        <Badge color="amber" className="gap-1">
          <Clock className="h-3 w-3" />
          Unknown
        </Badge>
      );
    case 'pending':
    default:
      return (
        <Badge color="zinc" className="gap-1">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
  }
};

// Format date helper
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Format relative time
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

// Collapsible Initial Task section - shows first line with expand button
const InitialTaskSection: React.FC<{ initialTask: string }> = ({
  initialTask,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Split the task into first line and rest
  const lines = initialTask.split('\n');
  const firstLine = lines[0] || '';
  const hasMoreContent = lines.length > 1 || firstLine.length > 100;

  return (
    <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-xl p-4 border border-zinc-200 dark:border-zinc-700">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
        Initial Task / Prompt
      </h3>
      <div className="text-sm text-zinc-700 dark:text-zinc-300">
        {isExpanded ? (
          <p className="whitespace-pre-wrap">{initialTask}</p>
        ) : (
          <p className="truncate">{firstLine}</p>
        )}
        {hasMoreContent && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                isExpanded && 'rotate-180',
              )}
            />
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
};

// ======================= DataTable Helpers =======================

// Filter key mapping: DataTable column id → backend query param
const FILTER_KEY_MAP: Record<string, string> = {
  created_by_name: 'created_by',
  firm_name: 'firm',
};

const REVERSE_FILTER_KEY_MAP = Object.fromEntries(
  Object.entries(FILTER_KEY_MAP).map(([k, v]) => [v, k]),
);

function getBackendFilterKey(columnId: string): string {
  return FILTER_KEY_MAP[columnId] ?? columnId;
}

function getColumnIdFromBackend(backendKey: string): string {
  return REVERSE_FILTER_KEY_MAP[backendKey] ?? backendKey;
}

// Query params builder
function toArray(
  value: string | string[] | boolean | number | null | undefined,
): string[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value.length > 0 ? value : undefined;
  return [String(value)];
}

function buildQueryParams(
  state: PaginationState,
): ListBulkAgentChatOperationsApiArg {
  const f = state.filters;
  return {
    limit: state.limit,
    offset: state.offset,
    ordering: state.ordering || undefined,
    status: toArray(f.status),
    createdBy: toArray(f.created_by),
    firm: toArray(f.firm),
    search: state.search || undefined,
  };
}

// Status filter options (static)
const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { label: 'Pending', value: 'pending' },
  { label: 'Running', value: 'running' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
];

// ======================= Cell Renderers =======================

const ProgressCell: React.FC<{ operation: BulkAgentChatOperationSchema }> = ({
  operation,
}) => {
  if (operation.created_count === 0 && operation.failed_count === 0)
    return <span className="text-xs text-zinc-500">—</span>;
  return (
    <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
      {operation.created_count} of {operation.total_count}
      {operation.failed_count > 0 && `, ${operation.failed_count} failed`}
    </span>
  );
};

// ======================= Column Definitions =======================

function createBulkOperationColumns(options: {
  creatorOptions: FilterOption[];
  firmOptions: FilterOption[];
}): TableColumn<BulkAgentChatOperationSchema>[] {
  return [
    // 1. Title — primary identifier
    {
      id: 'title',
      header: 'Title / Prompt',
      accessorFn: (row) => row.title,
      cell: (row) => (
        <div className="flex flex-col justify-center min-w-0">
          <span className="text-xs font-medium text-zinc-900 dark:text-white">
            {row.title}
          </span>
          <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 line-clamp-1">
            {row.initial_task}
          </span>
        </div>
      ),
      sortable: true,
      resizable: true,
      truncate: false,
    },
    // 2. Firm
    {
      id: 'firm_name',
      header: 'Firm',
      accessorFn: (row) => row.firm_name ?? '',
      cell: (row) => (
        <span className="text-xs font-medium text-zinc-900 dark:text-white">
          {row.firm_name || '—'}
        </span>
      ),
      filter: { type: 'multiselect', options: options.firmOptions },
      resizable: true,
      width: '280px',
    },
    // 3. Status
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: (row) => <OperationStatusBadge status={row.status} />,
      filter: { type: 'multiselect', options: STATUS_FILTER_OPTIONS },
      width: '110px',
      truncate: false,
    },
    // 4. Notion push status
    {
      id: 'readout_push_status',
      header: 'Notion Sync',
      accessorFn: (row) => row.readout_push_status ?? '',
      cell: (row) => {
        const labelMap: Record<string, string> = {
          pending: 'Pending',
          in_progress: 'Pushing',
          failed: 'Failed',
        };
        const isNoteworthy = row.readout_push_status != null && row.readout_push_status in labelMap;
        if (!isNoteworthy && !row.readout_notion_url) return null;
        if (row.readout_notion_url) {
          return (
            <a
              href={row.readout_notion_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none transition-colors"
              title="Open in Notion"
            >
              Pushed
              <ExternalLink className="h-3 w-3" />
            </a>
          );
        }
        return (
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {labelMap[row.readout_push_status!]}
          </span>
        );
      },
      width: '120px',
      truncate: false,
    },
    // 5. Progress
    {
      id: 'progress',
      header: 'Progress',
      accessorFn: (row) => row.created_count + row.failed_count,
      cell: (row) => <ProgressCell operation={row} />,
      width: '130px',
      truncate: false,
    },
    // 7. Created By
    {
      id: 'created_by_name',
      header: 'Created By',
      accessorFn: (row) => row.created_by_name ?? '',
      cell: (row) => (
        <span className="text-xs font-medium text-zinc-900 dark:text-white">
          {row.created_by_name || '—'}
        </span>
      ),
      filter: { type: 'multiselect', options: options.creatorOptions },
      width: '130px',
    },
    // 8. Created date
    {
      id: 'created_at',
      header: 'Created',
      accessorFn: (row) => row.created_at,
      cell: (row) => (
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          {formatRelativeTime(row.created_at)}
        </span>
      ),
      sortable: true,
      sort: {
        labels: { asc: 'Oldest First', desc: 'Newest First' },
        invertArrows: true,
      },
      width: '110px',
    },
  ];
}

// ======================= Main List Page =======================

export const BulkAgentOperationsPage: React.FC = () => {
  const navigate = useNavigate();

  // 1. URL-synced pagination state
  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 75,
    defaultSort: '-created_at',
  });

  // 2. Build query params
  const queryParams = useMemo(() => buildQueryParams(state), [state]);

  // 3. Fetch paginated data (with polling)
  const {
    data: rawData,
    isLoading,
    refetch,
    isFetching,
  } = useListBulkAgentChatOperationsQuery(queryParams, {
    pollingInterval: 10000,
    skipPollingIfUnfocused: true,
  });
  const operations = useMemo(() => getResults(rawData), [rawData]);
  const totalCount = getTotalCount(rawData);

  // 4. Fetch filter options
  const { data: filterOptionsData } =
    useBulkAgentOperationFilterOptionsQuery();
  const creatorOptions = useMemo(
    () =>
      (filterOptionsData?.creators ?? []).map((c) => ({
        label: c.name,
        value: c.id,
      })),
    [filterOptionsData?.creators],
  );
  const firmOptions = useMemo(
    () =>
      (filterOptionsData?.firms ?? []).map((f) => ({
        label: f.name,
        value: f.id,
      })),
    [filterOptionsData?.firms],
  );

  // 5. Column definitions (depend on filter options)
  const columns = useMemo(
    () => createBulkOperationColumns({ creatorOptions, firmOptions }),
    [creatorOptions, firmOptions],
  );

  // 6. Handlers
  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    const backendKey = getBackendFilterKey(columnId);
    const newFilters = { ...state.filters };
    if (value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[backendKey];
    } else {
      newFilters[backendKey] = value;
    }
    actions.setFilters(newFilters);
  };

  const handleSortChange = (columnId: string, direction?: 'asc' | 'desc') => {
    actions.setOrdering(columnId, direction);
  };

  const filterValues = useMemo(() => {
    const result: Record<string, string | string[] | null> = {};
    Object.entries(state.filters).forEach(([backendKey, value]) => {
      const columnId = getColumnIdFromBackend(backendKey);
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[columnId] = String(value);
      } else {
        result[columnId] = value;
      }
    });
    return result;
  }, [state.filters]);

  const currentSortField = useMemo(
    () => state.ordering.replace(/^-/, ''),
    [state.ordering],
  );

  // 7. Table actions
  const tableActions: TableAction[] = useMemo(
    () => [
      {
        label: 'Refresh',
        icon: (
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
        ),
        onClick: () => refetch(),
        variant: 'outline',
        color: 'zinc',
      },
      {
        label: 'New Operation',
        icon: <Plus className="h-4 w-4" />,
        onClick: () => navigate('/bulk-finchgpt-actions/create'),
        color: 'blue',
      },
    ],
    [isFetching, refetch, navigate],
  );

  return (
    <div className="flex flex-col gap-2 px-10 py-2 h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/20">
          <Bot className="h-4 w-4 text-white" />
        </div>
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">
          Manage Bulk FinchGPT Actions
        </h1>
      </div>

      {/* DataTable */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm">
        <DataTable
          data={operations}
          columns={columns}
          density="compact"
          getRowKey={(row) => row.id}
          isLoading={isLoading}
          actions={tableActions}
          searchEnabled
          searchPlaceholder="Search by title"
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          disableClientFilter
          paginationEnabled
          currentPage={actions.getCurrentPage()}
          totalPages={actions.getTotalPages(totalCount)}
          totalCount={totalCount}
          pageSize={state.limit}
          onPageChange={actions.setPage}
          onPageSizeChange={actions.setLimit}
          onSortChange={handleSortChange}
          sortField={currentSortField}
          sortDirection={state.ordering.startsWith('-') ? 'desc' : 'asc'}
          onFilterChange={handleFilterChange}
          filterValues={filterValues}
          onRowClick={(row) =>
            navigate(`/bulk-finchgpt-actions/${row.id}`)
          }
          emptyStateConfig={{
            icon: <Bot className="h-6 w-6 text-zinc-500" />,
            title: 'No operations found',
            description:
              'Bulk agent operations will appear here after you create them',
            action: {
              label: 'Create New Operation',
              onClick: () => navigate('/bulk-finchgpt-actions/create'),
            },
          }}
        />
      </div>
    </div>
  );
};

// Thread conversation panel component
const ThreadConversationPanel: React.FC<{
  selectedThread: AgentChatThreadSchema;
  onClose: () => void;
}> = ({ selectedThread, onClose }) => {
  return (
    <div className="h-full flex flex-col">
      {/* Header with close button */}
      <div className="px-4 py-2 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare className="h-4 w-4 text-zinc-500 flex-shrink-0" />
          <span className="text-sm font-medium text-zinc-900 dark:text-white truncate">
            {selectedThread.matter_name || 'Unnamed Matter'}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors"
          title="Close panel"
        >
          <X className="h-4 w-4 text-zinc-500" />
        </button>
      </div>
      {/* Chat content */}
      <div className="flex-1 min-h-0">
        <EditorProvider>
          <AgentChatThread
            matterId={selectedThread.matter_id}
            threadId={selectedThread.id}
            coverageId={selectedThread.coverage_id}
          />
        </EditorProvider>
      </div>
    </div>
  );
};

// Resizable column header component
const ResizableHeader: React.FC<{
  children: React.ReactNode;
  width: number;
  onResize: (newWidth: number) => void;
  minWidth?: number;
}> = ({ children, width, onResize, minWidth = 80 }) => {
  const [isResizing, setIsResizing] = React.useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + delta);
      onResize(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div className="relative flex items-center group" style={{ width }}>
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 truncate flex-1 pr-2">
        {children}
      </div>
      {/* Resize handle with grip indicator */}
      <div
        className={cn(
          'absolute right-0 top-0 bottom-0 w-2 cursor-col-resize flex items-center justify-center',
          'transition-colors',
          isResizing ? 'bg-blue-100' : 'hover:bg-zinc-100',
        )}
        onMouseDown={handleMouseDown}
      >
        {/* Grip dots */}
        <div className="flex flex-col gap-0.5">
          <div
            className={cn(
              'w-0.5 h-0.5 rounded-full transition-colors',
              isResizing
                ? 'bg-blue-500'
                : 'bg-zinc-300 group-hover:bg-zinc-400',
            )}
          />
          <div
            className={cn(
              'w-0.5 h-0.5 rounded-full transition-colors',
              isResizing
                ? 'bg-blue-500'
                : 'bg-zinc-300 group-hover:bg-zinc-400',
            )}
          />
          <div
            className={cn(
              'w-0.5 h-0.5 rounded-full transition-colors',
              isResizing
                ? 'bg-blue-500'
                : 'bg-zinc-300 group-hover:bg-zinc-400',
            )}
          />
        </div>
      </div>
    </div>
  );
};

// Threads table component with dynamic columns based on table definition
const ThreadsTable: React.FC<{
  threads: AgentChatThreadSchema[];
  selectedThread: AgentChatThreadSchema | null;
  onSelectThread: (thread: AgentChatThreadSchema) => void;
}> = ({ threads, selectedThread, onSelectThread }) => {
  // Get table definition from first thread (all threads in a bulk operation share the same definition)
  const tableDefinition = threads[0]?.table_definition;
  const tableColumns = tableDefinition?.columns ?? [];

  // Default column widths
  const defaultWidths = {
    matter: 200,
    title: 120,
    status: 100,
    created: 100,
    dynamic: 180,
  };

  // State for resizable column widths
  const [columnWidths, setColumnWidths] = React.useState<{
    matter: number;
    title: number;
    status: number;
    created: number;
    dynamic: number[];
  }>(() => ({
    matter: defaultWidths.matter,
    title: defaultWidths.title,
    status: defaultWidths.status,
    created: defaultWidths.created,
    dynamic: tableColumns.map(() => defaultWidths.dynamic),
  }));

  // Update dynamic column count when table definition changes
  React.useEffect(() => {
    setColumnWidths((prev) => ({
      ...prev,
      dynamic:
        prev.dynamic.length === tableColumns.length
          ? prev.dynamic
          : tableColumns.map(() => defaultWidths.dynamic),
    }));
  }, [tableColumns.length]);

  // Build grid template from current widths
  const dynamicColTemplate = columnWidths.dynamic
    .map((w) => `${w}px`)
    .join(' ');
  const gridTemplateColumns =
    `${columnWidths.matter}px ${columnWidths.title}px ${columnWidths.status}px ${dynamicColTemplate} ${columnWidths.created}px`.trim();

  const tableColumnIds = tableColumns.map((col) => col.id);

  // Export to CSV function
  const handleExportCsv = () => {
    // Build header row
    const headers = [
      'Matter',
      'Thread Title',
      'Status',
      ...tableColumnIds.map(humanizeColumnId),
      'Created',
    ];

    // Build data rows
    const rows = threads.map((thread) => {
      const dynamicValues = tableColumnIds.map(
        (colId) => thread.current_table_row?.[colId] ?? '',
      );
      return [
        thread.matter_name || 'Unnamed Matter',
        thread.title,
        thread.orchestrator_status,
        ...dynamicValues,
        thread.created_at,
      ];
    });

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCsvValue = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCsvValue).join(','),
      ...rows.map((row) => row.map(escapeCsvValue).join(',')),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulk-operation-threads-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 flex items-center justify-between flex-shrink-0">
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
          Created Threads ({threads.length})
        </h2>
        <Button
          outline
          onClick={handleExportCsv}
          className="gap-1.5 text-xs h-7 items-center"
          disabled={threads.length === 0}
        >
          <Download className="h-3.5 w-3.5" />
          <span>Export CSV</span>
        </Button>
      </div>

      {/* Scrollable table container */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-max">
          {/* Thread Table Header */}
          <div className="flex gap-4 px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-800/30 sticky top-0">
            <ResizableHeader
              width={columnWidths.matter}
              onResize={(w) =>
                setColumnWidths((prev) => ({ ...prev, matter: w }))
              }
            >
              Matter
            </ResizableHeader>
            <ResizableHeader
              width={columnWidths.title}
              onResize={(w) =>
                setColumnWidths((prev) => ({ ...prev, title: w }))
              }
            >
              Thread Title
            </ResizableHeader>
            <ResizableHeader
              width={columnWidths.status}
              onResize={(w) =>
                setColumnWidths((prev) => ({ ...prev, status: w }))
              }
              minWidth={90}
            >
              Status
            </ResizableHeader>
            {/* Dynamic column headers */}
            {tableColumns.map((col, idx) => (
              <ResizableHeader
                key={col.id}
                width={columnWidths.dynamic[idx] ?? defaultWidths.dynamic}
                onResize={(w) =>
                  setColumnWidths((prev) => ({
                    ...prev,
                    dynamic: prev.dynamic.map((dw, i) => (i === idx ? w : dw)),
                  }))
                }
              >
                <span title={col.prompt}>{humanizeColumnId(col.id)}</span>
              </ResizableHeader>
            ))}
            <ResizableHeader
              width={columnWidths.created}
              onResize={(w) =>
                setColumnWidths((prev) => ({ ...prev, created: w }))
              }
              minWidth={80}
            >
              Created
            </ResizableHeader>
          </div>

          {/* Thread Rows */}
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {threads.map((thread) => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                isSelected={selectedThread?.id === thread.id}
                onSelect={onSelectThread}
                tableColumnIds={tableColumnIds}
                gridTemplateColumns={gridTemplateColumns}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Detail page for a single operation
export const BulkAgentOperationDetailPage: React.FC = () => {
  const { operationId } = useParams<{ operationId: string }>();
  const navigate = useNavigate();
  const [shouldPoll, setShouldPoll] = React.useState(true);
  const [selectedThread, setSelectedThread] =
    React.useState<AgentChatThreadSchema | null>(null);

  // Template save modal state
  const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');

  // Push to Notion state
  const [notionPushStatus, setNotionPushStatus] = React.useState<
    'idle' | 'pushing' | 'done'
  >('idle');
  const [isNotionModalOpen, setIsNotionModalOpen] = React.useState(false);

  const {
    data: detail,
    isLoading,
    refetch,
    isFetching,
  } = enhancedApi.useGetBulkAgentChatOperationQuery(
    { id: operationId || '' },
    {
      skip: !operationId,
      pollingInterval: shouldPoll ? 3000 : undefined,
      skipPollingIfUnfocused: true,
    },
  );

  // Stop polling when operation is complete
  React.useEffect(() => {
    if (
      (detail?.status === 'completed' || detail?.status === 'failed') &&
      detail?.threads?.every(
        (thread) =>
          thread.orchestrator_status === 'completed' ||
          thread.orchestrator_status === 'failed' ||
          thread.orchestrator_status === 'unknown',
      )
    ) {
      setShouldPoll(false);
    }
  }, [detail?.status, detail?.threads]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-zinc-500">Loading operation details...</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
          <XCircle className="h-8 w-8 text-zinc-400" />
        </div>
        <p className="text-lg font-medium text-zinc-500">Operation not found</p>
        <Button outline onClick={() => navigate('/bulk-finchgpt-actions')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Operations
        </Button>
      </div>
    );
  }

  const progressPercent =
    detail.total_count > 0
      ? ((detail.created_count + detail.failed_count) / detail.total_count) *
        100
      : 0;

  return (
    <div className="flex flex-col gap-4 px-10 py-6 h-full">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/bulk-finchgpt-actions"
            className="p-2 -ml-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
            title="Back to Operations"
          >
            <ArrowLeft className="h-4 w-4 text-zinc-500" />
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-zinc-900 dark:text-white">
              {detail.title}
            </h1>
            <OperationStatusBadge status={detail.status} />
          </div>
        </div>

        {/* Compact Stats Bar */}
        <div className="flex items-center gap-4">
          {/* Progress pill */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full">
            <div className="w-16 h-1.5 bg-zinc-300 dark:bg-zinc-600 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  detail.status === 'completed' && 'bg-green-500',
                  detail.status === 'failed' && 'bg-red-500',
                  (detail.status === 'pending' ||
                    detail.status === 'running') &&
                    'bg-blue-500',
                )}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 tabular-nums">
              {detail.created_count + detail.failed_count}/{detail.total_count}
            </span>
          </div>

          {/* Success count */}
          {detail.created_count > 0 && (
            <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span className="text-xs font-medium tabular-nums">
                {detail.created_count}
              </span>
            </div>
          )}

          {/* Failed count */}
          {detail.failed_count > 0 && (
            <div className="flex items-center gap-1.5 text-red-500">
              <XCircle className="h-3.5 w-3.5" />
              <span className="text-xs font-medium tabular-nums">
                {detail.failed_count}
              </span>
            </div>
          )}

          <Button
            outline
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-1.5 text-xs h-8"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>

          {/* More actions dropdown */}
          <Dropdown>
            <DropdownButton outline className="h-8 w-8 !px-0">
              <EllipsisVertical className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set('title', detail.title);
                  params.set('initialTask', detail.initial_task);
                  const tableDefinition =
                    detail.threads?.[0]?.table_definition;
                  if (tableDefinition) {
                    params.set(
                      'tableDefinition',
                      JSON.stringify(tableDefinition),
                    );
                  }
                  navigate(
                    `/bulk-finchgpt-actions/create?${params.toString()}`,
                  );
                }}
              >
                <DropdownLabel>
                  <span className="flex items-center gap-2">
                    <Copy className="h-3.5 w-3.5" />
                    Duplicate
                  </span>
                </DropdownLabel>
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  setTemplateName(detail.title);
                  setIsTemplateModalOpen(true);
                }}
              >
                <DropdownLabel>
                  <span className="flex items-center gap-2">
                    <BookmarkPlus className="h-3.5 w-3.5" />
                    Save as Template
                  </span>
                </DropdownLabel>
              </DropdownItem>
              {(detail.status === 'completed' ||
                  detail.status === 'failed') && (
                  <>
                    <DropdownDivider />
                    {detail.notion_page_url && (
                      <DropdownItem
                        onClick={() =>
                          window.open(detail.notion_page_url!, '_blank')
                        }
                      >
                        <DropdownLabel>
                          <span className="flex items-center gap-2">
                            <ExternalLink className="h-3.5 w-3.5" />
                            View in Notion
                          </span>
                        </DropdownLabel>
                      </DropdownItem>
                    )}
                    <DropdownItem
                      onClick={() => setIsNotionModalOpen(true)}
                      disabled={notionPushStatus === 'pushing'}
                    >
                      <DropdownLabel>
                        <span className="flex flex-col gap-1">
                          <span className="flex items-center gap-2">
                            <ExternalLink className="h-3.5 w-3.5" />
                            Push to Notion
                          </span>
                          {detail.notion_push_status === 'failed' &&
                            detail.notion_push_error && (
                              <span className="text-xs font-medium text-red-600 ml-5.5 line-clamp-2">
                                Last push failed: {detail.notion_push_error}
                              </span>
                            )}
                        </span>
                      </DropdownLabel>
                    </DropdownItem>
                  </>
                )}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      {/* Save as Template Modal */}
      <Dialog
        open={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        size="sm"
      >
        <DialogTitle>Save as Template</DialogTitle>
        <DialogDescription>
          Save this operation configuration as a reusable template.
        </DialogDescription>
        <DialogBody>
          <Field>
            <Label>Template Name</Label>
            <Input
              autoFocus
              type="text"
              placeholder="e.g., Medical Records Review"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && templateName.trim()) {
                  const tableDefinition =
                    detail.threads?.[0]?.table_definition ?? null;
                  saveTemplate({
                    name: templateName.trim(),
                    title: detail.title,
                    initialTask: detail.initial_task,
                    tableDefinition,
                  });
                  setIsTemplateModalOpen(false);
                  setTemplateName('');
                  toast.success('Template saved', {
                    description: `"${templateName.trim()}" has been saved to your templates`,
                  });
                }
              }}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsTemplateModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            disabled={!templateName.trim()}
            onClick={() => {
              const tableDefinition =
                detail.threads?.[0]?.table_definition ?? null;
              saveTemplate({
                name: templateName.trim(),
                title: detail.title,
                initialTask: detail.initial_task,
                tableDefinition,
              });
              setIsTemplateModalOpen(false);
              setTemplateName('');
              toast.success('Template saved', {
                description: `"${templateName.trim()}" has been saved to your templates`,
              });
            }}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push to Notion Modal */}
      <NotionPushModal
        open={isNotionModalOpen}
        onClose={() => setIsNotionModalOpen(false)}
        operationId={operationId || ''}
        detail={detail}
        onPushComplete={refetch}
        onPushStatusChange={setNotionPushStatus}
      />

      {/* Initial Task */}
      <InitialTaskSection initialTask={detail.initial_task} />

      {/* Compact Error Banner */}
      {detail.error_details && detail.error_details.length > 0 && (
        <details className="group">
          <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors list-none">
            <XCircle className="h-3.5 w-3.5" />
            {detail.error_details.length} error
            {detail.error_details.length > 1 ? 's' : ''}
            <span className="group-open:rotate-90 transition-transform">
              ▶
            </span>
          </summary>
          <div className="mt-2 space-y-1.5 max-h-32 overflow-y-auto">
            {detail.error_details.map((error, idx) => (
              <div
                key={idx}
                className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg"
              >
                <span className="font-medium">Matter {error.matter_id}:</span>{' '}
                {error.error}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Threads Section */}
      <AgentChatProvider>
        <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm">
          {!detail.threads || detail.threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-zinc-500">
              <Clock className="h-8 w-8 mb-2 text-zinc-400" />
              <p className="text-sm">
                {detail.status === 'pending' || detail.status === 'running'
                  ? 'Threads are being created...'
                  : 'No threads were created'}
              </p>
            </div>
          ) : selectedThread ? (
            /* Split-pane view when thread is selected */
            <ResizablePanelGroup direction="horizontal" className="h-[500px]">
              {/* Left Panel - Thread List */}
              <ResizablePanel defaultSize={40} minSize={25} maxSize={60}>
                <ThreadsTable
                  threads={detail.threads}
                  selectedThread={selectedThread}
                  onSelectThread={setSelectedThread}
                />
              </ResizablePanel>

              <ResizableHandle withHandle />

              {/* Right Panel - Conversation Viewer */}
              <ResizablePanel defaultSize={60} minSize={40}>
                <ThreadConversationPanel
                  selectedThread={selectedThread}
                  onClose={() => setSelectedThread(null)}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            /* Full-width table view when no thread is selected */
            <ThreadsTable
              threads={detail.threads}
              selectedThread={null}
              onSelectThread={setSelectedThread}
            />
          )}
        </div>
      </AgentChatProvider>
    </div>
  );
};

// Thread row component
const ThreadRow: React.FC<{
  thread: AgentChatThreadSchema;
  isSelected?: boolean;
  onSelect?: (thread: AgentChatThreadSchema) => void;
  tableColumnIds?: string[];
  gridTemplateColumns: string;
}> = ({
  thread,
  isSelected,
  onSelect,
  tableColumnIds,
  gridTemplateColumns,
}) => {
  return (
    <button
      type="button"
      onClick={() => onSelect?.(thread)}
      className={cn(
        'grid gap-4 px-4 py-3 transition-colors w-full text-left',
        isSelected
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50 border-l-2 border-transparent',
      )}
      style={{ gridTemplateColumns }}
    >
      <div className="flex items-center min-w-0">
        <MatterLink entityId={thread.matter_id} className="text-sm truncate">
          {thread.matter_name || 'Unnamed Matter'}
        </MatterLink>
      </div>
      <div className="flex items-center min-w-0">
        <span className="text-sm text-zinc-700 dark:text-zinc-300 truncate">
          {thread.title}
        </span>
      </div>
      <div className="flex items-center">
        <ThreadStatusBadge status={thread.orchestrator_status} />
      </div>
      {/* Dynamic table columns */}
      {tableColumnIds?.map((columnId) => {
        const value = thread.current_table_row?.[columnId] ?? '—';
        return (
          <div
            key={columnId}
            className="flex items-center min-w-0 group/cell relative"
          >
            <span className="text-sm text-zinc-600 dark:text-zinc-400 truncate">
              {value}
            </span>
            {/* Tooltip on hover - only show if value is not empty placeholder */}
            {value !== '—' && (
              <div className="absolute left-0 bottom-full mb-1 hidden group-hover/cell:block z-50 pointer-events-none">
                <div className="bg-zinc-900 text-white text-xs px-2 py-1.5 rounded shadow-lg max-w-xs whitespace-pre-wrap">
                  {value}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className="flex items-center">
        <span className="text-sm text-zinc-500 dark:text-zinc-400">
          {formatRelativeTime(thread.created_at)}
        </span>
      </div>
    </button>
  );
};
